/**
 * TaskTable — flat table with auto-width columns.
 *
 * Column width strategy:
 *   - table-layout: auto  →  browser sizes each column to its content
 *   - Task <col>          →  gets a width hint (user-resizable, stored in widths.task)
 *                             all other columns shrink/grow to fit their cell content
 *   - Resize handle shown only on Task column
 *
 * Filters live entirely in the page header — this component is filter-display only.
 * Loading state renders skeleton rows (same geometry as real rows) so the
 * table doesn't collapse → no layout shift when data arrives.
 */

import { useMemo, useRef, useCallback } from 'react';
import TaskRow from './Taskrow.jsx';
import EmptyState from '../ui/Emptystate.jsx';
import Icon from '../ui/Icons.jsx';

/* ── Resize handle (Task column only) ─────────────────────── */
function ResizeHandle({ colId, currentWidth, onResize }) {
  const rafId = useRef(null);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startW = currentWidth;

    document.body.style.userSelect = 'none';
    document.body.style.cursor     = 'col-resize';

    const onMove = (me) => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        onResize(colId, startW + (me.clientX - startX));
      });
    };

    const onUp = () => {
      document.body.style.userSelect = '';
      document.body.style.cursor     = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
  }, [colId, currentWidth, onResize]);

  return (
    <div
      className="absolute right-0 top-0 h-full w-2 flex items-center justify-center cursor-col-resize select-none group/rh"
      onMouseDown={handleMouseDown}
    >
      <div className="w-px h-4 bg-slate-300 group-hover/rh:bg-[color:var(--accent)] transition-colors rounded-full" />
    </div>
  );
}

/* ── Skeleton rows shown while loading ────────────────────── */
function SkeletonRows({ columns, rows = 6 }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r} className="orin-tr">
          {columns.map((col) => (
            <td key={col.id} className="orin-td px-3 py-2.5 border-b border-border-base">
              <div
                className="orin-skeleton"
                style={{ width: col.id === 'task' ? '85%' : '70%' }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/* ── TaskTable ──────────────────────────────────────────────── */
export default function TaskTable({
  tasks = [],
  loading = false,
  error = null,
  columns = [],
  widths = {},
  onColumnResize,
  statusFilter   = 'all',
  priorityFilter = 'all',
  ownerFilter    = 'all',
  onRemark,
  onDelete,
}) {
  const hasActions = Boolean(onRemark || onDelete);
  /* Client-side filter — memoised so unrelated re-renders skip the pass */
  const filtered = useMemo(
    () =>
      tasks.filter((t) => {
        const statusOk   = statusFilter   === 'all' || t.status   === statusFilter;
        const priorityOk = priorityFilter === 'all' || t.priority === priorityFilter;
        const ownerOk    = ownerFilter    === 'all' || t.owner_id === ownerFilter;
        return statusOk && priorityOk && ownerOk;
      }),
    [tasks, statusFilter, priorityFilter, ownerFilter],
  );

  const taskColWidth = widths?.task ?? 300;

  return (
    <div className="card overflow-hidden">
      {error ? (
        <div className="py-12 px-6 text-center">
          <p className="text-sm text-status-overdue mb-2">Failed to load tasks</p>
          <p className="text-xs text-muted">{error}</p>
        </div>
      ) : !loading && filtered.length === 0 ? (
        <EmptyState
          icon={<Icon name="sheet" size={20} />}
          title="No tasks match the current filters"
          subtitle="Try adjusting the filters in the page header."
        />
      ) : (
        <div className="orin-table-scroll">
          <table className="w-full border-collapse">
            {/* Task column hint — all other columns auto-size to content */}
            <colgroup>
              {columns.map((col) =>
                col.id === 'task'
                  ? <col key="task" style={{ width: taskColWidth }} />
                  : <col key={col.id} />
              )}
              {hasActions && <col key="actions" style={{ width: 70 }} />}
            </colgroup>

            <thead className="sticky top-0 z-10 orin-table-head">
              <tr className="border-b border-border-base">
                {columns.map((col) => (
                  <th
                    key={col.id}
                    className="orin-th text-left px-3 py-2.5 relative select-none whitespace-nowrap"
                    scope="col"
                    style={col.id === 'task' ? { minWidth: taskColWidth } : undefined}
                  >
                    {col.label}
                    {col.id === 'task' && onColumnResize && (
                      <ResizeHandle
                        colId="task"
                        currentWidth={taskColWidth}
                        onResize={onColumnResize}
                      />
                    )}
                  </th>
                ))}
                {hasActions && (
                  <th className="orin-th text-left px-2 py-2.5 select-none whitespace-nowrap" scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                )}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <SkeletonRows columns={hasActions ? [...columns, { id: '__actions' }] : columns} />
              ) : (
                filtered.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    columns={columns}
                    taskMinWidth={taskColWidth}
                    latestComment={task.latest_comment ?? task.latestComment}
                    onRemark={onRemark}
                    onDelete={onDelete}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
